import React, { useState, useEffect } from 'react'
import axios from 'axios'
import styled from 'styled-components'

const isDev = import.meta.env.DEV
const docsApiUrl = isDev ? 'http://localhost:8081/docs' : 'https://api.browniepoints.africa/docs'

// icons
import { Close } from '@styled-icons/evil/Close'

// utils
import th from 'utils/themeHelper'

const CloseIcon = styled(Close)`
  border: 1px solid ${th('borders.mid')};
  border-radius: 3px;
  color: ${props => (props.dark ? th('text.dark') : 'white')};
  cursor: pointer;
  float: right;
  margin-right: 0;
  margin-top: 0;

  &:hover {
    border: 1px solid ${th('borders.darkPill')};
    color: ${props => (props.dark ? 'white' : th('text.dark'))};
    background: #444;
  }
`

const HelpContainerOuter = styled.div`
  display: block;
  height: calc(100% - 80px);
  left: 4px;
  position: fixed;
  top: 65px;
  width: calc(100% - 8px);
  z-index: 8;

  @media (min-width: 769px) {
    right: 0;
    height: 100%;
    margin-top: 0;
    top: 0;
  }
`

const HelpContainerInner = styled.div`
  background: white;
  border: 1px solid ${th('borders.mid')};
  border-radius: 3px;
  display: flex;
  height: 100%;
  margin: 5px auto;
  overflow: scroll;
  padding: 20px;

  @media (min-width: 769px) {
    margin: 5px;
    height: calc(100% - 80px);

    width: calc(100% - 10px);
    right: 0;
    top: 65px;
    position: fixed;
  }
`

// const HelpContainerMainHeader = styled.div`
//   font-family: ${th('fonts.bold')};
//   font-size: 30px;
//   line-height: 1;
//   margin-bottom: 20px;
// `

// const HelpContainerSubHeader = styled.div`
//   font-family: ${th('fonts.bold')};
//   font-size: 20px;
//   line-height: 1;
//   margin-bottom: 20px;
//   margin-top: 20px;
// `

const DocsPageContentContainer = styled.div`
  display: block;
  height: 100%;
  width: 100%;
`

const DocsPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
`

const TagsContainer = styled.div`
  padding-bottom: 40px;
  padding-top: 40px;
`

const NavContainer = styled.div`
  margin-bottom: 15px;
`

const TagsHeader = styled.div`
  font-size: 22px;
  line-height: 1.2;
  margin: 0 0 10px;
  font-family: ${th('fonts.bold')};
`

const SingleTag = styled.span`
  border: 1px solid ${th('borders.darkPill')};
  color: ${th('text.dark')};
  cursor: pointer;
  border-radius: 3px;
  display: inline-block;
  font-size: 14px;
  margin-bottom: 8px;
  margin-right: 8px;
  padding: 6px 12px;
  text-align: center;
  text-transform: uppercase;

  &:hover {
    border-color: ${th('primary.base')};
    background: ${th('primary.base')};
    color: white;
  }
`

const ActiveSingleTag = styled(SingleTag)`
  border-color: ${th('primary.base')};
  background: ${th('primary.base')};
  color: white;
  cursor: default;
`

const DocsPageContentWrapper = styled.div`
  flex: 1;
  padding: 10px;
  position: relative;

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 20px 0;
    font-family: Arial, sans-serif;
  }

  th,
  td {
    border: 1px solid #dddddd;
    text-align: left;
    padding: 8px;
  }

  th {
    background-color: #f2f2f2;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  code {
    background-color: #f8f8f8;
    border-radius: 4px;
    border: 1px solid ${th('borders.mid')};
    color: ${th('text.mid')};
    font-size: 12px;
    padding: 3px 5px;
    white-space: pre-wrap;
  }

  h1 {
    font-size: 22px;
    line-height: 1.2;
    margin: 0 0 8px;
    font-family: ${th('fonts.bold')};
    text-decoration: underline;
  }

  h2 {
    font-size: 16px;
    line-height: 1.2;
    margin: 4px 0 8px;
  }

  p {
    margin: 0;
  }

  @media screen and (min-width: 769px) {
    padding: 0 0 10px;

    h1 {
      font-size: 40px;
      line-height: 1.2;
      margin: 0 0 15px;
    }

    h2 {
      font-size: 30px;
      line-height: 1.2;
      margin: 20px 0 10px;
    }
  }
`

const HelpNav = ({ selectedPageName, visiblePage, setSelectedPageName }) => {
  const isOnAllPagesPage = selectedPageName === 'all'
  if (isOnAllPagesPage) {
    return (
      <NavContainer>
        <ActiveSingleTag>All Pages</ActiveSingleTag>
      </NavContainer>
    )
  }

  const visiblePageIdentifier = visiblePage ? visiblePage.identifier : null

  return (
    <NavContainer>
      <SingleTag onClick={() => setSelectedPageName('all')}>All Pages</SingleTag>

      {visiblePageIdentifier && <ActiveSingleTag>{visiblePage.name}</ActiveSingleTag>}
    </NavContainer>
  )
}

const TagSection = ({ pages = [], setSelectedPageName, tags = [], tagsHeader }) => {
  if (!tags.length) {
    return (
      <TagsContainer>
        <TagsHeader>{tagsHeader}</TagsHeader>

        <SingleTag onClick={() => setSelectedPageName('all')}>View All Help Pages</SingleTag>
      </TagsContainer>
    )
  }

  return (
    <TagsContainer>
      <TagsHeader>{tagsHeader}</TagsHeader>

      {tags.map(t => {
        const page = pages.find(p => p.identifier === t)

        return (
          <SingleTag key={t} onClick={() => setSelectedPageName(t)}>
            {page.name}
          </SingleTag>
        )
      })}
    </TagsContainer>
  )
}

const MarkdownContent = ({ visiblePage = {}, pages, selectedPageName, setSelectedPageName }) => {
  const isViewingAll = selectedPageName === 'all'
  if (isViewingAll) {
    const allPageTags = pages.map(p => p.identifier)

    return (
      <DocsPageContentContainer>
        <HelpNav
          selectedPageName={selectedPageName}
          setSelectedPageName={setSelectedPageName}
          visiblePage={visiblePage}
        />

        <DocsPageContentWrapper>
          <div>All Pages</div>
        </DocsPageContentWrapper>

        <TagSection
          pages={pages}
          setSelectedPageName={setSelectedPageName}
          tags={allPageTags}
          tagsHeader="All pages"
        />
      </DocsPageContentContainer>
    )
  }

  const content = visiblePage.content || ''
  const tags = visiblePage.tags || []

  if (!visiblePage.id) {
    return (
      <DocsPageContentContainer>
        <HelpNav
          selectedPageName={selectedPageName}
          setSelectedPageName={setSelectedPageName}
          visiblePage={visiblePage}
        />

        <DocsPageContentWrapper>
          <div>
            Help is not available for this page yet. Please reach out to notify us that you need
            help with this feature.
          </div>
        </DocsPageContentWrapper>

        <TagSection setSelectedPageName={setSelectedPageName} />
      </DocsPageContentContainer>
    )
  }

  return (
    <DocsPageContentContainer>
      <HelpNav
        selectedPageName={selectedPageName}
        setSelectedPageName={setSelectedPageName}
        visiblePage={visiblePage}
      />

      <DocsPageContentWrapper>
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </DocsPageContentWrapper>

      <TagSection
        pages={pages}
        setSelectedPageName={setSelectedPageName}
        tags={tags}
        tagsHeader="Related Tags"
      />
    </DocsPageContentContainer>
  )
}

const deriveVisiblePage = ({ pages, selectedPageName }) => {
  const selectedPage = pages.find(p => {
    const targetPathLowerCase = (p.primary_target_path || '').toLowerCase()
    const identifierLowerCase = (p.identifier || '').toLowerCase()
    return targetPathLowerCase === selectedPageName || identifierLowerCase === selectedPageName
  })
  if (selectedPageName) return selectedPage

  const pathname = window.location.pathname.toLowerCase()
  const trimmedPathname = pathname.replace('/dashboard/', '').replace(/\d+/, 'id')

  const relevantPage = pages.find(p => {
    const targetPath = p.primary_target_path || ''
    return targetPath.toLowerCase() === trimmedPathname
  })
  const fallbackPage = pages.find(p => {
    const targetPath = p.primary_target_path || ''
    return targetPath.toLowerCase() === 'how-to-guides'
  })

  return selectedPage || relevantPage || fallbackPage
}

const HelpModal = ({ setHelpModalState, showModal }) => {
  if (!showModal) return null

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setHelpModalState(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  // State to store the fetched data
  const [selectedPageName, setSelectedPageName] = useState(null)
  // State to store the fetched data
  const [pages, setPages] = useState([])
  // State to store loading status
  const [isLoading, setIsLoading] = useState(true)
  // State to store any error
  const [error, setError] = useState(null)

  useEffect(() => {
    // Function to fetch data from the server
    const fetchData = async () => {
      try {
        const { data, status } = await axios.get(docsApiUrl, { withCredentials: true })

        if (status !== 200) {
          throw new Error(`HTTP error! status: ${status}`)
        }

        setPages(data)
      } catch (error) {
        setError(error.message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, []) // Empty dependency array means this effect runs once on mount

  if (isLoading) return <div>Loading...</div>
  if (error) return <div>Error: {error}</div>

  const visiblePage = deriveVisiblePage({ pages, selectedPageName })

  return (
    <HelpContainerOuter>
      <HelpContainerInner>
        <DocsPageContainer>
          <MarkdownContent
            visiblePage={visiblePage}
            pages={pages}
            selectedPageName={selectedPageName}
            setSelectedPageName={setSelectedPageName}
          />
        </DocsPageContainer>

        <CloseIcon dark size={30} onClick={() => setHelpModalState(false)} />
      </HelpContainerInner>
    </HelpContainerOuter>
  )
}

export default HelpModal
